// Derives the mode switcher's Live entry disabled state and user-facing
// tooltip from the live_mode subscription entitlement. Extracted out of
// NavSideBar.vue so ApplicationLayout.vue (which now owns the mode switcher,
// via NavRail) can drive it too, and so this logic is unit-testable without
// mounting either component.
import { computed } from "vue";

import { useLiveModeEntitlement } from "@/composables/live-mode-entitlement";
import { useUserManager } from "@/stores/user-store";

export const useLiveModeTooltip = () => {
    const { liveModeEnabled, liveModeLoading, liveModeUnavailableReason } =
        useLiveModeEntitlement();
    const { currentOrganizationPermissions } = useUserManager();

    // Fail closed while the entitlement is still loading: enabling the Live
    // entry until the fetch resolves lets an un-entitled org click into Live
    // for a moment.
    const liveModeDisabled = computed(
        () => liveModeLoading.value || !liveModeEnabled.value
    );

    /**
     * Where to send someone who cannot use Live mode.
     *
     * Subscribing lives at Organization Settings → Billing & Subscription, and
     * that page is billing-manager-only — so pointing an ordinary member at it
     * sends them to a door they cannot open. Tell them who can instead.
     *
     * A platform-wide kill-switch is a different situation entirely: the
     * subscription is untouched, buying more changes nothing, and checkout
     * refuses. Offering "subscribe" there sends the customer down a dead end
     * at the worst possible moment, so it gets its own line. The tooltip
     * deliberately keeps returning `undefined` while loading — during that
     * window we do not yet know a subscription is missing, so telling the
     * user to subscribe would be wrong.
     */
    const liveModeTooltip = computed(() => {
        if (liveModeLoading.value || liveModeEnabled.value) return undefined;
        if (liveModeUnavailableReason.value === "globally_disabled") {
            return "Live Mode is temporarily unavailable across the platform. Your subscription is unchanged.";
        }
        return currentOrganizationPermissions.value?.can_manage_billing
            ? "Subscribe to Live Mode in Organization Settings → Billing & Subscription to enable the Live view."
            : "Ask a billing manager to subscribe to Live Mode to enable the Live view.";
    });

    return { liveModeDisabled, liveModeTooltip };
};
